import { SmartConnector } from '__pages/SmartConnectors/types';
import { StyledSettingsContentCard } from '../styles';
import { SubrouteComponent } from '../types';
import SmartConnectorService from 'services/SmartConnectorService';
import { getErrorMessage } from '__hooks/useErrors';
import { useToast } from '__components/ToastProvider';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toastVariant } from '__components/ToastProvider';
import { SubSectionWithHeader as SubSection } from 'components/Layout/SubSection';
import Cols from '__components/Layout/Cols';
import InputControl from '__components/Inputs/InputControl';
import { getOriginalError } from '__services/AxiosService';
import { SmartConnectorNameInput } from '../../../components/SmartConnectorNameInput';
import { StatusText } from '__pages/SmartConnectors/components/StatusText';
import useField from '__hooks/useField';
import { EmailInput } from '../EmailInput';
import { MaxConcurrentExecutionsInput } from '../MaxConcurrentExecutionsInput';
import { WebhookURLInput } from '../WebhookURLInput';
import { Description } from 'pages/Common/components/Description';
import { ApiNameInput } from '__pages/SmartConnectors/components/ApiNameInput';

export const Settings: SubrouteComponent = ({
  smartConnector,
  setSmartConnector,
  access,
}) => {
  const { t } = useTranslation();
  const [showToast] = useToast();

  const [errors, setErrors] = useState<Record<string, string>>({ name: '' });
  const [patching, setPatching] = useState(false);

  const [name, setName] = useField(smartConnector.name);
  const [errorEmail, setErrorEmail] = useField(
    smartConnector.error_notification_email
  );
  const [allEmail, setAllEmail] = useField(
    smartConnector.all_notification_email
  );
  const [maxConcurrentExecutions, setMaxConcurrentExecutions] = useField(
    smartConnector.max_concurrent_executions
  );
  const [webhookUrl, setWebhookUrl] = useField(
    smartConnector.execution_ended_webhook_url ?? ''
  );

  const patchSmartConnector = async (payload: Partial<SmartConnector>) => {
    setPatching(true);
    try {
      const { data } = await SmartConnectorService.updateSmartConnector(
        smartConnector.id,
        payload
      );
      setSmartConnector(data);

      return true;
    } catch (err: any) {
      const orig = getOriginalError(err);
      if (
        orig?.name ||
        orig?.error_notification_email ||
        orig?.all_notification_email ||
        orig?.api_name ||
        orig?.max_concurrent_executions ||
        orig?.execution_ended_webhook_url ||
        orig?.description
      ) {
        setErrors((prev) => ({
          ...prev,
          name: orig.name || '',
          api_name: orig.api_name || '',
          error_notification_email: orig.error_notification_email || '',
          all_notification_email: orig.all_notification_email || '',
          max_concurrent_executions: orig.max_concurrent_executions || '',
          execution_ended_webhook_url: orig.execution_ended_webhook_url || '',
          description: orig.description || '',
        }));
      } else {
        showToast({
          message:
            getErrorMessage(orig) || t('Failed to update Smart Connector'),
          variant: toastVariant.FAILURE,
        });
      }
      return false;
    } finally {
      setPatching(false);
    }
  };

  const onChangeName = (value: string) => {
    setName(value);
    setErrors((prev) => ({ ...prev, name: '' }));
  };

  const patchName = async () => {
    const trimmed = name.trim();
    setName(trimmed);
    if (smartConnector.name !== trimmed) {
      patchSmartConnector({ name: trimmed });
    }
  };

  const onChangeErrorEmail = (value: string) => {
    setErrorEmail(value);
    setErrors((prev) => ({ ...prev, error_notification_email: '' }));
  };

  const onChangeAllEmail = (value: string) => {
    setAllEmail(value);
    setErrors((prev) => ({ ...prev, all_notification_email: '' }));
  };

  const patchErrorEmail = async () => {
    const trimmed = (errorEmail || '').trim();
    setErrorEmail(trimmed);
    if (smartConnector.error_notification_email !== trimmed) {
      patchSmartConnector({ error_notification_email: trimmed });
    }
  };

  const patchAllEmail = async () => {
    const trimmed = (allEmail || '').trim();
    setAllEmail(trimmed);
    if (smartConnector.all_notification_email !== trimmed) {
      patchSmartConnector({ all_notification_email: trimmed });
    }
  };

  const onChangeMaxConcurrentExecutions = (value: number) => {
    setMaxConcurrentExecutions(value);
    setErrors((prev) => ({ ...prev, max_concurrent_executions: '' }));
  };

  const patchMaxConcurrentExecutions = async () => {
    if (smartConnector.max_concurrent_executions !== maxConcurrentExecutions) {
      patchSmartConnector({
        max_concurrent_executions: maxConcurrentExecutions,
      });
    }
  };

  const onChangeWebhookUrl = (value: string) => {
    setWebhookUrl(value);
    setErrors((prev) => ({ ...prev, execution_ended_webhook_url: '' }));
  };

  const patchWebhookUrl = async () => {
    const trimmed = (webhookUrl || '').trim();
    setWebhookUrl(trimmed);
    if (smartConnector.execution_ended_webhook_url !== trimmed) {
      patchSmartConnector({ execution_ended_webhook_url: trimmed });
    }
  };

  const patchDescription = async (description: string) => {
    setErrors((prev) => ({ ...prev, description: '' }));
    if (smartConnector.description !== description) {
      await patchSmartConnector({ description });
    }
  };

  return (
    <StyledSettingsContentCard>
      <SubSection title={t('SmartConnector Settings')} action={null}>
        <Cols columns={3} gutter="20px">
          <InputControl margin>
            <SmartConnectorNameInput
              variant="outline"
              error={errors.name}
              value={name}
              onChange={onChangeName}
              data-qa="smartconnector-name-input"
              onBlur={patchName}
              disabled={!access.edit || patching}
            />
          </InputControl>
          <ApiNameInput value={smartConnector.api_name} />
          <InputControl label={t('Status')}>
            <StatusText status={smartConnector.status} />
          </InputControl>
        </Cols>
        <Description
          label={t('Description')}
          description={smartConnector?.description || ''}
          aiDescription={smartConnector?.ai_description}
          onChange={patchDescription}
          inputProps={{
            margin: true,
            disabled: !access.edit || patching,
          }}
          error={errors.description}
        />
        <Cols columns={3} gutter="20px">
          <MaxConcurrentExecutionsInput
            value={maxConcurrentExecutions}
            onChange={onChangeMaxConcurrentExecutions}
            error={errors.max_concurrent_executions}
            onBlur={patchMaxConcurrentExecutions}
            disabled={!access.edit || patching}
          />
          <EmailInput
            value={allEmail}
            error={errors.all_notification_email}
            onChange={onChangeAllEmail}
            onBlur={patchAllEmail}
            disabled={!access.edit || patching}
            labelInfo={t(
              'Notification emails will always be sent to the uploading user by default, but you may also add another email recipient. This address will receive both Success and Error notifications.'
            )}
            label={t('Additional Notification Email')}
            margin
          />
          <EmailInput
            value={errorEmail}
            error={errors.error_notification_email}
            onChange={onChangeErrorEmail}
            onBlur={patchErrorEmail}
            disabled={!access.edit || patching}
            labelInfo={t(
              'Error emails will always be sent to the uploading user by default, but you may also add another email recipient (such as a distribution group).'
            )}
            label={t('Additional Error Notification Email')}
          />
        </Cols>
        <Cols columns={3} gutter="20px">
          <WebhookURLInput
            value={webhookUrl}
            error={errors.execution_ended_webhook_url}
            onChange={onChangeWebhookUrl}
            onBlur={patchWebhookUrl}
            disabled={!access.edit || patching}
          />
        </Cols>
      </SubSection>
    </StyledSettingsContentCard>
  );
};
